import { useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { signInWithGoogle, useAuth } from '../auth/store.js';

/** Reasons the callback can bounce back here, as user-facing sentences. */
const ERROR_MESSAGES: Record<string, string> = {
  not_allowed:
    'That Google account is not on the guest list yet. Ask whoever runs this table to add your email address.',
  auth_failed: 'Sign-in did not complete. Please try again.',
};

export function LoginPage() {
  const status = useAuth((s) => s.status);
  const refresh = useAuth((s) => s.refresh);
  const [params] = useSearchParams();
  const location = useLocation() as { state?: { from?: string } };

  useEffect(() => {
    if (status === 'unknown') void refresh();
  }, [status, refresh]);

  if (status === 'authenticated') {
    return <Navigate to={location.state?.from ?? '/'} replace />;
  }

  const error = params.get('error');
  const message = error ? (ERROR_MESSAGES[error] ?? ERROR_MESSAGES['auth_failed']) : null;

  return (
    <section className="mx-auto max-w-sm space-y-6 py-12">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          This table is invite-only. Sign in with the Google account whose address was added to
          the guest list.
        </p>
      </div>

      {message && (
        <p
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          {message}
        </p>
      )}

      <button
        type="button"
        onClick={() => signInWithGoogle(location.state?.from ?? '/')}
        className="w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-900 transition hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800"
      >
        Sign in with Google
      </button>
    </section>
  );
}
